import { useEffect } from 'react';
import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button } from '@frontend/design-system';
import { FilterableCheckboxList } from '../../../components/filterable-checkbox-list';
import { useGetCalendarV3QueriesData } from '../../../hooks/scheduler-v3/use-get-calendar-v3-queries-data';
import { AppointmentStatusFilterLabel } from '../appointment-status-filter-label';
import { EntityFilterItemLabel } from '../entity-filter-item-label';
import { useFilterPanelForm } from '../use-filter-panel-form';
import { PopoverFilterPanelContentV2 } from './popover-filter-panel-content-v2';
import { PopoverMenuButton } from './popover-menu-button';

interface Props {
  onClose: () => void;
}

const PopoverFilterPanelContentV3 = ({ onClose }: Props) => {
  const { t } = useTranslation('schedule');
  const { formField, isFiltersChanged, filterCounts, applyFilters, resetFilters, syncFilters } = useFilterPanelForm();
  const { filterLists } = useGetCalendarV3QueriesData();
  const { isV3OperatorySupported } = SchedulerV3.Helpers.useCheckV3OperatorySupport();

  useEffect(() => {
    syncFilters();
    // NOTE: sync only when open the drawer to avoid overwrite unsaved changes
    // So don't add syncFilters to dependency array
  }, []);

  const handleApplyFilters = () => {
    applyFilters();
    onClose();
  };
  const handleResetFilters = () => {
    resetFilters();
    onClose();
  };

  return (
    <>
      <PopoverMenuButton
        iconName='pending-small'
        text={t('Appointment Status')}
        filterCount={filterCounts.apptStatuses}
      >
        <FilterableCheckboxList
          listStyle={listStyle}
          fieldName='apptStatuses'
          checkListField={formField.getFieldProps('apptStatuses')}
          list={filterLists.appointmentStatuses}
          Label={AppointmentStatusFilterLabel}
        />
      </PopoverMenuButton>
      <PopoverMenuButton iconName='tile-small' text={t('Appointment Types')} filterCount={filterCounts.apptTypes}>
        <FilterableCheckboxList
          listStyle={listStyle}
          fieldName='apptTypes'
          checkListField={formField.getFieldProps('apptTypes')}
          list={filterLists.appointmentTypes}
          Label={EntityFilterItemLabel}
        />
      </PopoverMenuButton>
      <PopoverMenuButton iconName='user-small' text={t('Providers')} filterCount={filterCounts.providers}>
        <FilterableCheckboxList
          listStyle={listStyle}
          fieldName='providers'
          checkListField={formField.getFieldProps('providers')}
          list={filterLists.providers}
          Label={EntityFilterItemLabel}
        />
      </PopoverMenuButton>
      <PopoverMenuButton
        iconName='shield-small'
        text={t('Insurance Status')}
        disabled
        filterCount={filterCounts.insuranceStatuses}
      />
      <PopoverMenuButton
        iconName='department-small'
        text={t('Operatories')}
        filterCount={filterCounts.operatories}
        disabled={!isV3OperatorySupported}
      >
        <FilterableCheckboxList
          listStyle={listStyle}
          fieldName='operatories'
          checkListField={formField.getFieldProps('operatories')}
          list={filterLists.operatories}
        />
      </PopoverMenuButton>
      <div css={{ display: 'flex', gap: theme.spacing(1), marginTop: theme.spacing(1) }}>
        <Button variant='secondary' css={{ flex: 1 }} onClick={handleResetFilters} disabled={!filterCounts.total}>
          {t('Reset')}
        </Button>
        <Button css={{ flex: 1 }} onClick={handleApplyFilters} disabled={!isFiltersChanged}>
          {t('Apply')}
        </Button>
      </div>
    </>
  );
};

export const PopoverFilterPanelContent = (props: Props) => {
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();

  if (!isScheduleV3FlagEnabled) {
    return <PopoverFilterPanelContentV2 {...props} />;
  }

  return <PopoverFilterPanelContentV3 {...props} />;
};

const listStyle = css({
  minHeight: 200,
  maxHeight: 400,
  overflowY: 'auto',
});
